// src/views/AuthPanel.tsx · 自写登录 / 注册面板（EP-010-S4）
//
// ★ 老板 2026-09-25 拍板：**选 B 自写 UI**（不用 Cognito Hosted UI），**先用最简单的 UI**。
//   "最简单"= 朴素表单：邮箱 + 密码 + 两个按钮（登录 / 注册）。
//   不要动画、不要组件库、不要表单验证库、不要密码强度条、不要 loading 骨架屏。
//
// ★ "最简单"**不等于**"可以跳过"（别理解错）：
//   本面板是 UX 文档 §2.2 的全屏强制弹窗 + §2.3 路由级锁，Esc 无效、遮罩点击无效、
//   **没有任何"跳过 / 以后再说"按钮** —— 唯一的关闭路径是登录成功（§7 F6 硬验收）。
//   这里的实现比 F6 更彻底：**根本没有 onClose 这个 prop**，想关也关不掉。
//
// ★ §v3-J 真相源原则（backend-proposal.md §v3-J.1 / §v3-J.2）：
//   本组件**不读浏览器本地存储做登录态判定**。登录成功 = 云端 POST /auth/login 返回 200；
//   是否真的登录 = 由 AuthGuard 的 GET /auth/me 云端返回判定。本地一行存储都不碰。
//   （本文件连"本地存储"这个 API 名都不出现 —— 验收可直接 grep 验证为 0 处。）
//
// ★ 文案纪律：以下 AUTH_COPY **逐字**取自 design/ux/08-auth-onboarding.md §6.2，
//   一个字都不改（含标点）。改文案必须先改 UX 文档，再同步这里。

import { useState } from 'react';
import { login, register } from '../api/auth';
import { ApiError } from '../api/types';

// ───────────────────────────────────────────────────────────────────────────
// UX §6.2 文案（逐字引用，禁止就地改写）
// ───────────────────────────────────────────────────────────────────────────

export const AUTH_COPY = {
  auth01: '把进度存起来',
  auth02: '用邮箱建一个账号，换设备也能接着看。',
  auth03: '邮箱',
  auth04: '密码',
  auth05: '用邮箱注册',
  auth06: '登录',
  auth13: '好了。以后换设备，它去过的地方还在。',
  auth14: '回来了。它去过的地方都还在。',
  auth15: '邮箱或密码对不上，再试一次。',
  auth16: '这个邮箱已经注册过了。要不要直接登录？',
  auth18: '连不上。检查一下网络，再试一次。',
  auth19: '密码再长一点，或者换几个字符。',
  auth20: '这个邮箱看着不太对，再看一眼。',
  auth21: '正在登录，稍等。',
  auth25: '继续，表示同意《隐私政策》',
  auth26: '账号信息保存在海外服务器。',
} as const;

// ───────────────────────────────────────────────────────────────────────────
// 错误 → 文案映射（UX §6.2：失败态一律客观白描，不写"错误 / 失败"）
// ───────────────────────────────────────────────────────────────────────────

function messageFor(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 0) return AUTH_COPY.auth18; // 网络不通 / DNS / abort
    if (err.status === 401) return AUTH_COPY.auth15; // 邮箱或密码不对（Cognito 归一）
    if (err.status >= 500) return AUTH_COPY.auth18; // 后端挂了，玩家视角就是"连不上"
    if (err.status === 400) {
      // 400 不再一刀切成 auth-19（密码问题）。
      // 后端把 Cognito 原始错误名透传在响应 body.error（如 NotAuthorizedException /
      // InvalidPasswordException / UsernameExistsException）；ApiError 把它挂在
      // readonly body 字段上（src/api/types.ts:133），按名字区分：
      const b = err.body as { error?: unknown; message?: unknown } | undefined;
      const code = typeof b?.error === 'string' ? b.error : '';
      const serverMsg = typeof b?.message === 'string' ? b.message : '';
      // 真正的密码策略问题才显示 auth-19
      if (code === 'InvalidPasswordException') return AUTH_COPY.auth19;
      // 邮箱已注册（后端 UsernameExists 走 200，这里是兜底）
      if (code === 'UsernameExistsException') return AUTH_COPY.auth16;
      // 其余 400（如 NotAuthorizedException「SignUp is not permitted」）=
      // 服务端/配置类问题，不是玩家输入错。优先显示后端 message（真实原因），
      // 没有 message 才退回 auth-18（玩家视角"连不上"）。
      // 注：这是开发期定位用；生产不应把内部错误暴露给玩家，S7/后续可归一。
      if (serverMsg.length > 0) {
        return `${AUTH_COPY.auth18}（${serverMsg}）`;
      }
      return AUTH_COPY.auth18;
    }
  }
  return AUTH_COPY.auth18;
}

// ───────────────────────────────────────────────────────────────────────────
// 组件
// ───────────────────────────────────────────────────────────────────────────

export interface AuthPanelProps {
  /** 登录成功回调（AuthGuard 收到后重查云端 /auth/me → 放行主视图） */
  onAuthed: () => void;
}

export function AuthPanel({ onAuthed }: AuthPanelProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // 手写两行校验（不引表单验证库 —— 老板要"最简单"）：
  //   邮箱 = 有 @ 且有 . ；密码 = ≥ 8 位（与 Cognito 用户池策略一致，auth.ts:110）
  function checkFields(): string | null {
    if (!email.includes('@') || !email.includes('.')) return AUTH_COPY.auth20;
    if (password.length < 8) return AUTH_COPY.auth19;
    return null;
  }

  async function handleLogin(): Promise<void> {
    const invalid = checkFields();
    if (invalid !== null) {
      setError(invalid);
      return;
    }
    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      await login(email, password);
      setNotice(AUTH_COPY.auth14);
      onAuthed();
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRegister(): Promise<void> {
    const invalid = checkFields();
    if (invalid !== null) {
      setError(invalid);
      return;
    }
    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      await register(email, password);
      // UX §3.6：不单独弹欢迎页，注册完直接进（这里立刻补一次登录，玩家零额外点击）
      setNotice(AUTH_COPY.auth13);
      try {
        await login(email, password);
        onAuthed();
      } catch {
        // 注册成功却登录失败 ⇒ 这个邮箱早就有账号了，只是密码对不上
        setError(AUTH_COPY.auth16);
      }
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-panel">
      {/* ★ 无 onClick 遮罩、无 Esc 监听 ⇒ §7 F6「不可跳过」在结构上成立，无法被绕过 */}
      <div
        className="auth-panel-modal"
        role="dialog"
        aria-modal="true"
        aria-label={AUTH_COPY.auth01}
      >
        <p className="auth-panel__title">{AUTH_COPY.auth01}</p>
        <p className="auth-panel__subtitle">{AUTH_COPY.auth02}</p>

        {/* noValidate：关掉浏览器原生校验气泡 —— 否则邮箱格式不对时浏览器会自弹
            「请输入电子邮件地址」并**拦下提交**，UX §6 auth-20 的文案永远显示不出来。
            校验仍由 checkFields() 手写兜住，文案 100% 走 UX 定稿。 */}
        <form
          className="auth-panel__form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void handleLogin();
          }}
        >
          <label className="auth-panel__label" htmlFor="auth-email">
            {AUTH_COPY.auth03}
          </label>
          <input
            id="auth-email"
            className="auth-panel__input"
            type="email"
            autoComplete="email"
            autoFocus
            disabled={submitting}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <label className="auth-panel__label" htmlFor="auth-password">
            {AUTH_COPY.auth04}
          </label>
          <input
            id="auth-password"
            className="auth-panel__input"
            type="password"
            autoComplete="current-password"
            disabled={submitting}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <button type="submit" className="auth-panel__primary" disabled={submitting}>
            {AUTH_COPY.auth06}
          </button>
          <button
            type="button"
            className="auth-panel__secondary"
            disabled={submitting}
            onClick={() => void handleRegister()}
          >
            {AUTH_COPY.auth05}
          </button>
        </form>

        {submitting ? (
          <p className="auth-panel__notice" role="status">
            {AUTH_COPY.auth21}
          </p>
        ) : null}
        {notice !== null ? (
          <p className="auth-panel__notice" role="status">
            {notice}
          </p>
        ) : null}
        {error !== null ? (
          <p className="auth-panel__error" role="alert">
            {error}
          </p>
        ) : null}

        <p className="auth-panel__fine">{AUTH_COPY.auth25}</p>
        <p className="auth-panel__fine">{AUTH_COPY.auth26}</p>
      </div>
    </div>
  );
}
